import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, Button, Tabs, Tab, Alert, Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import TargetTable from '../../components/library/TargetTable';
import TemplateTable from '../../components/library/TemplateTable';
import TargetForm from '../../components/library/TargetForm';
import TemplateForm from '../../components/library/TemplateForm';
import QuickSubmitDialog from '../../components/library/QuickSubmitDialog';
import { MOON_PEAK_DEFAULT } from '../../constants/moon';

const Library = () => {
  const navigate = useNavigate();
  const { user, updateUserPreferences } = useAuth();
  const currentUserId = user?.id;
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Data
  const [targets, setTargets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [observatories, setObservatories] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  // Target state
  const [targetSearch, setTargetSearch] = useState('');
  const [targetFilterType, setTargetFilterType] = useState('all');
  const [targetFilterProject, setTargetFilterProject] = useState('all');
  const [targetFormOpen, setTargetFormOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);

  // Template state
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateFilterProject, setTemplateFilterProject] = useState('');
  const [templateFilterObservatory, setTemplateFilterObservatory] = useState('');
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Quick submit
  const [quickSubmitTemplate, setQuickSubmitTemplate] = useState(null);

  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirm, setConfirm] = useState(null);

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ──────────────────────────────────────────
  // Data loading
  // ──────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [targetRes, templateRes, projectRes, obsRes, orgRes] = await Promise.all([
        apiService.getLibraryTargets({ limit: 100 }),
        apiService.getLibraryTemplates({ limit: 100 }),
        apiService.getAllProjects(),
        apiService.getObservatories(),
        apiService.getUserOrganizations().catch(() => ({ data: { organizations: [] } })),
      ]);
      setTargets(targetRes.data?.targets || []);
      setTemplates(templateRes.data?.templates || []);
      setProjects(Array.isArray(projectRes) ? projectRes : []);
      const obsList = obsRes.data?.observatories || [];
      setObservatories(obsList);
      setOrganizations(orgRes.data?.organizations || orgRes.data || []);

      // Pre-select saved observatory preference for template filter
      const savedObsId = user?.preferences?.last_observatory_id;
      if (savedObsId && obsList.find(o => o._id === savedObsId)) {
        setTemplateFilterObservatory(savedObsId);
      }
    } catch (err) {
      console.error('Failed to load library data:', err);
      showMessage('Failed to load library data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ──────────────────────────────────────────
  // Target actions
  // ──────────────────────────────────────────

  const handleCreateTarget = async (data) => {
    try {
      await apiService.createLibraryTarget(data);
      showMessage('Target added to library');
      setTargetFormOpen(false);
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Failed to create target', 'error');
    }
  };

  const handleUpdateTarget = async (data) => {
    try {
      await apiService.updateLibraryTarget(editingTarget._id, data);
      showMessage('Target updated');
      setEditingTarget(null);
      setTargetFormOpen(false);
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Failed to update target', 'error');
    }
  };

  const handleDeleteTarget = (target) => {
    setConfirm({
      title: 'Delete Target',
      message: (
        <span>Delete <strong>{target.name}</strong> from the library?</span>
      ),
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          await apiService.deleteLibraryTarget(target._id);
          showMessage('Target deleted');
          loadData();
        } catch (err) {
          showMessage('Failed to delete target', 'error');
          throw err;
        }
      },
    });
  };

  const handleEditTarget = (target) => {
    setEditingTarget(target);
    setTargetFormOpen(true);
  };

  const handleNewTemplateFromTarget = (target) => {
    // Pre-fill template form with this target
    setEditingTemplate(null);
    setTemplateFormOpen(true);
    // The form will be opened on the templates tab with the target pre-loaded
    // We'll pass the target data via a temporary state
    setTab(1);
    // Set a small delay to allow tab switch
    setTimeout(() => {
      setEditingTemplate({
        _prefillTarget: target, // Signal to TemplateForm
      });
      setTemplateFormOpen(true);
    }, 100);
  };

  // ──────────────────────────────────────────
  // Template actions
  // ──────────────────────────────────────────

  const handleCreateTemplate = async (data) => {
    try {
      await apiService.createLibraryTemplate(data);
      showMessage('Template created');
      setTemplateFormOpen(false);
      setEditingTemplate(null);
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Failed to create template', 'error');
    }
  };

  const handleUpdateTemplate = async (data) => {
    try {
      await apiService.updateLibraryTemplate(editingTemplate._id, data);
      showMessage('Template updated');
      setEditingTemplate(null);
      setTemplateFormOpen(false);
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Failed to update template', 'error');
    }
  };

  const handleDeleteTemplate = (template) => {
    setConfirm({
      title: 'Delete Template',
      message: (
        <span>Delete template <strong>{template.name}</strong>?</span>
      ),
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          await apiService.deleteLibraryTemplate(template._id);
          showMessage('Template deleted');
          loadData();
        } catch (err) {
          showMessage('Failed to delete template', 'error');
          throw err;
        }
      },
    });
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateFormOpen(true);
  };

  const handleQuickSubmit = async (template) => {
    try {
      const result = await apiService.activateLibraryTemplate(template._id);
      showMessage(`Observation created: ${result.data?.observation_id}`);
      setQuickSubmitTemplate(null);
      loadData();
    } catch (err) {
      throw new Error(err.response?.data?.error?.message || 'Failed to create observation');
    }
  };

  const handleEditAndSubmit = (template) => {
    // Pre-fill observation form via sessionStorage and navigate
    const prefillData = {
      assignedTo: template.observatory_id?._id || template.observatory_id || '',
      targets: template.targets.map(t => ({
        name: t.name,
        naif_id: t.naif_id || '',   // #1409: carry spacecraft NAIF id from template into the prefilled form
        ra: t.ra.toString(),
        dec: t.dec.toString(),
        epoch: t.epoch || 'J2000',
        positionAngle: t.rotation || 0,
        priority: t.priority || 5,
        sequenceRepeatCount: t.sequence_repeat_count || 1,
        exposureConfigs: (t.exposure_configs || []).map(ec => ({
          filter: ec.filter,
          exposureTime: ec.exposure_time,
          exposureCount: ec.exposure_count,
          binning: ec.binning?.toString() || '1',
          gain: ec.gain ?? null,
          offset: ec.offset ?? null,
        })),
        constraints: t.constraints || {},
      })),
      minAltitude: template.targets[0]?.constraints?.minimum_altitude || 30,
      maxAirmass: template.targets[0]?.constraints?.maximum_airmass || 2.0,
      moonSeparation: template.targets[0]?.constraints?.moon_separation || MOON_PEAK_DEFAULT,
      guidingEnabled: template.guiding_options?.enabled ?? true,
      ditherEnabled: template.guiding_options?.dither_enabled ?? false,
      initialAutofocus: template.autofocus_options?.initial_autofocus ?? true,
      autofocusInterval: template.autofocus_options?.interval_minutes ?? 30,
    };

    // Find project name for pre-fill
    const project = projects.find(p => {
      const templateProjectId = template.project_id?._id || template.project_id;
      return p._id === templateProjectId;
    });
    if (project) {
      prefillData.projectName = project.name;
    }

    sessionStorage.setItem('prefillObservation', JSON.stringify(prefillData));
    navigate('/observations/new');
  };

  // ──────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Target Library</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (tab === 0) {
                setEditingTarget(null);
                setTargetFormOpen(true);
              } else {
                setEditingTemplate(null);
                setTemplateFormOpen(true);
              }
            }}
          >
            {tab === 0 ? 'Add Target' : 'New Template'}
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Targets (${targets.length})`} />
        <Tab label={`Templates (${templates.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && (
            <TargetTable
              targets={targets}
              projects={projects}
              loading={loading}
              currentUserId={currentUserId}
              onEdit={handleEditTarget}
              onDelete={handleDeleteTarget}
              onNewTemplate={handleNewTemplateFromTarget}
              onRefresh={loadData}
              searchTerm={targetSearch}
              onSearchChange={setTargetSearch}
              filterType={targetFilterType}
              onFilterTypeChange={setTargetFilterType}
              filterProject={targetFilterProject}
              onFilterProjectChange={setTargetFilterProject}
            />
          )}

          {tab === 1 && (
            <TemplateTable
              templates={templates}
              projects={projects}
              observatories={observatories}
              loading={loading}
              currentUserId={currentUserId}
              onEdit={handleEditTemplate}
              onDelete={handleDeleteTemplate}
              onQuickSubmit={(t) => setQuickSubmitTemplate(t)}
              onEditAndSubmit={handleEditAndSubmit}
              onRefresh={loadData}
              searchTerm={templateSearch}
              onSearchChange={setTemplateSearch}
              filterProject={templateFilterProject}
              onFilterProjectChange={setTemplateFilterProject}
              filterObservatory={templateFilterObservatory}
              onFilterObservatoryChange={(v) => { setTemplateFilterObservatory(v); if (v) updateUserPreferences({ last_observatory_id: v }); }}
            />
          )}
        </>
      )}

      {/* Target Form Dialog */}
      {targetFormOpen && (
        <TargetForm
          open={targetFormOpen}
          onClose={() => { setTargetFormOpen(false); setEditingTarget(null); }}
          onSave={editingTarget ? handleUpdateTarget : handleCreateTarget}
          target={editingTarget}
          projects={projects}
          observatories={observatories}
          organizations={organizations}
        />
      )}

      {/* Template Form Dialog */}
      {templateFormOpen && (
        <TemplateForm
          open={templateFormOpen}
          onClose={() => { setTemplateFormOpen(false); setEditingTemplate(null); }}
          onSave={editingTemplate?._id ? handleUpdateTemplate : handleCreateTemplate}
          organizations={organizations}
          template={editingTemplate?._prefillTarget ? {
            targets: [{
              library_target_id: editingTemplate._prefillTarget._id,
              name: editingTemplate._prefillTarget.name,
              ra: editingTemplate._prefillTarget.ra,
              dec: editingTemplate._prefillTarget.dec,
              epoch: editingTemplate._prefillTarget.epoch || 'J2000',
              rotation: editingTemplate._prefillTarget.rotation || 0,
              priority: 5,
              sequence_repeat_count: 1,
              exposure_configs: [],
              constraints: editingTemplate._prefillTarget.constraints || {},
            }],
            project_id: editingTemplate._prefillTarget.project_id,
          } : editingTemplate}
          projects={projects}
          observatories={observatories}
          libraryTargets={targets}
        />
      )}

      {/* Quick Submit Dialog */}
      <QuickSubmitDialog
        open={!!quickSubmitTemplate}
        onClose={() => setQuickSubmitTemplate(null)}
        template={quickSubmitTemplate}
        onConfirm={handleQuickSubmit}
      />

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
};

export default Library;
